// src/components/Common/LazyLoader.jsx
// Enhanced Lazy Loading Component with Error Boundary and Performance Monitoring

import React, { Suspense } from 'react';
import { motion } from 'framer-motion';
import { trackBusinessEvents } from '../../utils/analytics';

// Enhanced Loading Spinner Component
const EnhancedLoadingSpinner = ({ 
  message = "Loading...", 
  component = "page",
  minimal = false 
}) => {
  if (minimal) {
    return (
      <div className="minimal-loader" style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '2px solid #f3f3f3',
          borderTop: '2px solid #D40523',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
      </div>
    );
  }

  return (
    <motion.div 
      className="enhanced-loading-spinner"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: component === 'page' ? '60vh' : '200px',
        flexDirection: 'column',
        background: component === 'page' ? 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' : 'transparent',
        color: '#333'
      }}
    >
      {/* Animated Spinner */}
      <motion.div
        className="spinner"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e9ecef',
          borderTop: '4px solid #D40523',
          borderRadius: '50%',
          marginBottom: '20px'
        }}
      />
      
      {/* Loading Message */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{
          fontSize: '16px',
          fontFamily: "'Open Sans', sans-serif",
          color: '#666',
          margin: 0,
          textAlign: 'center'
        }}
      >
        {message}
      </motion.p>
      
      {/* Brand Touch */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        transition={{ delay: 0.5 }}
        style={{
          marginTop: '10px',
          fontSize: '12px',
          color: '#999',
          fontFamily: "'Exo', sans-serif"
        }}
      >
        ADTAC - Food Safety Excellence
      </motion.div>
    </motion.div>
  );
};

// Error Boundary Component
class LazyLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Track loading errors for performance monitoring
    trackBusinessEvents.errorEvent('lazy_load_error', error.message, this.props.componentName || 'unknown');
    
    console.error('Lazy load error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="lazy-load-error" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          flexDirection: 'column',
          padding: '40px',
          textAlign: 'center',
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          margin: '20px'
        }}>
          <h3 style={{ color: '#dc3545', marginBottom: '10px' }}>Loading Error</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Sorry, there was an issue loading this content.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#D40523',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontFamily: "'Exo', sans-serif",
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main Lazy Loader Wrapper
export const LazyLoader = ({ 
  children, 
  loadingMessage = "Loading...", 
  componentName = "component",
  minimal = false 
}) => {
  return (
    <LazyLoadErrorBoundary componentName={componentName}>
      <Suspense fallback={
        <EnhancedLoadingSpinner 
          message={loadingMessage} 
          component={componentName}
          minimal={minimal}
        />
      }>
        {children}
      </Suspense>
    </LazyLoadErrorBoundary>
  );
};

// Higher-Order Component for Route Lazy Loading
export const withLazyLoading = (Component, loadingMessage, componentName) => {
  return React.forwardRef((props, ref) => (
    <LazyLoader loadingMessage={loadingMessage} componentName={componentName}>
      <Component {...props} ref={ref} />
    </LazyLoader>
  ));
};

// Pre-loading utilities for critical routes
export const preloadRoute = (routeImport) => {
  // Preload the route component
  return routeImport();
};

// Performance monitoring hook
export const useLoadingPerformance = (componentName) => {
  React.useEffect(() => {
    const startTime = performance.now();
    
    return () => {
      const loadTime = performance.now() - startTime;
      trackBusinessEvents.componentLoad(componentName, loadTime);
    };
  }, [componentName]);
};

export default LazyLoader;